import React, { useState, useEffect } from "react";
import {
  ChevronDown,
  Star,
  MapPin,
  Calendar,
  Users,
  Phone,
  Mail,
  Instagram,
  Facebook,
  Twitter,
  Menu,
  X,
  Play,
} from "lucide-react";
import BookingButton from "../Components/BookingButton";
import FloatingWhatsappButton from "../Components/FloatingWhatsappButton";

export default function DubaiTourismHomepage() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);

  const handleSmoothScroll = (e, targetId) => {
    e.preventDefault();

    const element = document.querySelector(targetId);
    if (!element) return;

    element.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });

    setIsMenuOpen(false); // Also close the menu
  };

  const heroSlides = [
    {
      image:
        "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80",
      title: "Discover the Magic of Dubai",
      subtitle: "Unforgettable Adventures Await",
    },
    {
      image: "/images/ferrari.png",
      title: "Luxury Rides & Experiences",
      subtitle: "Unleash the Thrill of Dubai in Style",
    },
    {
      image:
        "https://images.unsplash.com/photo-1518684079-3c830dcef090?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2126&q=80",
      title: "Desert Safari Adventures",
      subtitle: "Experience the Thrill of the Dunes",
    },
    {
      image:
        "https://images.unsplash.com/photo-1566073771259-6a8506099945?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80",
      title: "Luxury & Modern Marvels",
      subtitle: "Where Dreams Meet Reality",
    },
  ];

  const services = [
    {
      title: "Desert Safari",
      image: "/images/group-people-sitting-top-dune.jpg",
      description:
        "Experience heart-pounding adventures in Dubai's golden dunes with our premium safari packages.",
    },
    {
      title: "Theme Parks",
      image: "/images/theme-park.jpg",
      description:
        "Thrilling rides and magical experiences at Dubai's world-class theme parks and attractions.",
    },
    {
      title: "Cruise Tours",
      image: "/images/cruise-tours.jpeg",
      description:
        "Sail across Dubai's waters in luxury with our unforgettable cruise tours and dinner experiences.",
    },
    {
      title: "Sea Plane & Helicopter Ride",
      image: "/images/jetski.jpeg",
      description:
        "Enjoy a thrilling water jet ride followed by stunning aerial views from a helicopter.",
    },
    {
      title: "Water Sports",
      image: "/images/water-sports.jpg",
      description:
        "Dive into excitement with jet skiing, parasailing, and water adventures along Dubai's coast.",
    },
    {
      title: "City Tours",
      image: "/images/city-tour.jpg",
      description:
        "Explore Dubai's iconic landmarks, from towering skyscrapers to traditional souks.",
    },
    {
      title: "Visa Services",
      image: "/images/⁮visa-services.jpg",
      description:
        "Seamless and stress-free visa processing for your Dubai travel plans.",
    },
    {
      title: "Beach Resorts",
      image: "/images/beach-resort.jpg",
      description:
        "Relax and unwind at Dubai's luxurious beachfront resorts and private beaches.",
    },

    {
      title: "Flight Tickets",
      image: "/images/flight-tickets.jpg",
      description: "Quick and hassle-free bookings.",
    },
    {
      title: "Hotel Reservations",
      image: "/images/hotel-reservations.jpg",
      description: "Exclusive stays at top UAE hotels.",
    },
    {
      title: "VIP Transport",
      image: "/images/vip-transport.jpg",
      description: "Limo, chauffeur, and private car rentals.",
    },
    {
      title: "MICE Organizer",
      image: "/images/mice.jpg",
      description:
        "Expert planning and management of Meetings, Incentives, Conferences, and Events across Dubai, ensuring seamless and memorable experiences for your business and corporate needs.",
    },
  ];

  const testimonials = [
    {
      name: "Sarah Johnson",
      country: "United States",
      rating: 5,
      text: "Absolutely incredible experience! The desert safari was breathtaking and the service was top-notch.",
    },
    {
      name: "Ahmed Al-Mansouri",
      country: "UAE",
      rating: 5,
      text: "Professional team with amazing attention to detail. Made our family vacation unforgettable!",
    },
    {
      name: "Emma Thompson",
      country: "United Kingdom",
      rating: 5,
      text: "From booking to experience, everything was seamless. Dubai's beauty exceeded all expectations.",
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <FloatingWhatsappButton />
      {/* Navigation */}
      <nav className="fixed top-0 w-full backdrop-blur-md shadow-lg z-50 transition-all duration-300 pb-1 pt-1" style={{ backgroundColor: '#E6210E' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-12">
            {/* Logo + Text */}
            <div className="flex items-center mb-2 mt-2">
              <div className="flex flex-col items-center">
                <img
                  src="/images/atw_logo.png"
                  alt="Around The World Logo"
                  className="w-24 h-8"
                />
                <div className="text-[10px] font-bold text-white">
                  Around The World Travel &amp; Tourism
                </div>


              </div>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:block">
              <div className="ml-10 flex items-center space-x-8">
                <a
                  href="#home"
                  className="text-gray-900 hover:text-blue-600 transition-colors font-medium"
                >
                  Home
                </a>
                <a
                  href="#services"
                  className="text-gray-900 hover:text-blue-600 transition-colors font-medium"
                >
                  Services
                </a>
                <a
                  href="#whyus"
                  className="text-gray-900 hover:text-blue-600 transition-colors font-medium"
                >
                  Why Us
                </a>
                <a
                  href="#about"
                  className="text-gray-900 hover:text-blue-600 transition-colors font-medium"
                >
                  About Us
                </a>
                <a
                  href="#contact"
                  className="text-gray-900 hover:text-blue-600 transition-colors font-medium"
                >
                  Contact
                </a>
                {/* <button className="bg-gradient-to-r from-blue-600 to-green-500 text-white px-6 py-2 rounded-full hover:shadow-lg transform hover:scale-105 transition-all duration-300 font-medium">
            Book Now
          </button> */}
                <BookingButton />
              </div>
            </div>

            {/* Mobile Hamburger */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-gray-900 hover:text-blue-600 transition-colors"
              >
                {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {isMenuOpen && (
          <div
            className={`md:hidden transition-all duration-500 ease-in-out bg-white border-t border-gray-200 ${isMenuOpen
              ? "max-h-96 opacity-100 overflow-visible"
              : "max-h-0 opacity-0 overflow-hidden"
              }`}
          >
            <div className="px-2 pt-2 pb-3 space-y-1">
              <a
                href="#home"
                onClick={(e) => handleSmoothScroll(e, "#home")}
                className="block px-3 py-2 text-gray-900 hover:text-blue-600 transition-colors"
              >
                Home
              </a>
              <a
                href="#services"
                onClick={(e) => handleSmoothScroll(e, "#services")}
                className="block px-3 py-2 text-gray-900 hover:text-blue-600 transition-colors"
              >
                Services
              </a>
              <a
                href="#about"
                onClick={(e) => handleSmoothScroll(e, "#about")}
                className="block px-3 py-2 text-gray-900 hover:text-blue-600 transition-colors"
              >
                About Us
              </a>
              <a
                href="#whyus"
                onClick={(e) => handleSmoothScroll(e, "#whyus")}
                className="block px-3 py-2 text-gray-900 hover:text-blue-600 transition-colors"
              >
                Why Us
              </a>
              <a
                href="#contact"
                onClick={(e) => handleSmoothScroll(e, "#contact")}
                className="block px-3 py-2 text-gray-900 hover:text-blue-600 transition-colors"
              >
                Contact
              </a>

              {/* Render booking button as is, don't close menu immediately */}
              <BookingButton />
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative h-screen overflow-hidden">
        <div className="absolute inset-0">
          {heroSlides.map((slide, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ${index === activeSlide ? "opacity-100" : "opacity-0"
                }`}
            >
              <div
                className="w-full h-full bg-cover bg-center bg-no-repeat"
                style={{ backgroundImage: `url(${slide.image})` }}
              />
              <div className="absolute inset-0 bg-black/40" />
            </div>
          ))}
        </div>

        <div className="relative z-10 flex items-center justify-center h-full text-center text-white px-4">
          <div className="max-w-4xl mx-auto">
            <div className="mb-6 animate-fade-in">
              <span className="inline-block px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium mb-4">
                ⭐ Dubai's Top Rated Travel Agency
              </span>
            </div>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight animate-slide-up">
              {heroSlides[activeSlide].title}
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-gray-200 animate-slide-up delay-100">
              {heroSlides[activeSlide].subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-up delay-200">
              <button className="bg-gradient-to-r from-blue-600 to-green-500 text-white px-8 py-4 rounded-full font-semibold text-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300">
                <a href="#services">Explore Adventures</a>
              </button>
              {/* <button className="border-2 border-white text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-white hover:text-gray-900 transition-all duration-300 flex items-center justify-center gap-2">
                <Play size={20} />
                Watch Video
              </button> */}
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <ChevronDown className="text-white w-8 h-8" />
        </div>

        {/* Slide indicators */}
        <div className="absolute bottom-4 right-4 flex space-x-2">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => setActiveSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${index === activeSlide ? "bg-white" : "bg-white/50"
                }`}
            />
          ))}
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Unforgettable{" "}
              <span className="bg-gradient-to-r from-blue-600 to-green-500 bg-clip-text text-transparent">
                Experiences
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From heart-pounding desert safaris to luxurious beach resorts,
              discover Dubai's diverse attractions at unbeatable prices
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-200 flex flex-col group" // 👈 Added 'group' here
              >
                {/* Image Section */}
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover transform transition-transform duration-500 group-hover:scale-110" // 👈 Zoom on hover
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent px-4 py-3">
                    <h3 className="text-white text-xl font-bold">
                      {service.title}
                    </h3>
                  </div>
                </div>

                {/* Description & Button — same as before */}
                <div className="flex flex-col justify-between flex-grow p-6">
                  <p className="text-gray-700 leading-relaxed mb-6">
                    {service.description}
                  </p>

                  <div className="text-center mt-auto">
                    <a
                      href="#contact"
                      className="inline-block bg-gradient-to-r from-blue-600 to-green-500 text-white px-8 py-3 rounded-full font-semibold shadow-md hover:shadow-xl transform hover:scale-105 transition-all duration-300 ease-in-out"
                    >
                      Contact Now
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section
        id="whyus"
        className="py-20 bg-gradient-to-br from-blue-50 to-green-50"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Why Choose{" "}
              <span className="bg-gradient-to-r from-blue-600 to-green-500 bg-clip-text text-transparent">
                Around The World?
              </span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center group">
              <div className="bg-white w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg group-hover:shadow-xl transition-all duration-300">
                <Star className="w-10 h-10 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Top Rated
              </h3>
              <p className="text-gray-600">
                Dubai's highest rated travel agency with thousands of satisfied
                customers
              </p>
            </div>

            <div className="text-center group">
              <div className="bg-white w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg group-hover:shadow-xl transition-all duration-300">
                <Users className="w-10 h-10 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Trust & Safety
              </h3>
              <p className="text-gray-600">
                Your safety and peace of mind throughout your travel experience
              </p>
            </div>

            <div className="text-center group">
              <div className="bg-white w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg group-hover:shadow-xl transition-all duration-300">
                <Calendar className="w-10 h-10 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Fast Booking
              </h3>
              <p className="text-gray-600">
                Lightning-fast booking process to secure your dream itinerary
              </p>
            </div>

            <div className="text-center group">
              <div className="bg-white w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg group-hover:shadow-xl transition-all duration-300">
                <MapPin className="w-10 h-10 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Local Expertise
              </h3>
              <p className="text-gray-600">
                Passionate travel experts with deep knowledge of Dubai
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section
        id="about"
        className="py-20 bg-gradient-to-br from-blue-50 to-green-50"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-3">
              About{" "}
              <span className="bg-gradient-to-r from-blue-600 to-green-500 bg-clip-text text-transparent">
                Around The World
              </span>
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto leading-relaxed">
              Welcome to <strong>Around The World Tourism LLC</strong>, your
              trusted Destination Management Company approved by Dubai Tourism.
              We specialize in crafting unforgettable business and leisure
              experiences across the UAE — from daily tours and excursions to
              luxury safaris, dhow cruises, and MICE events. With a dedicated
              24/7 team, we ensure every journey is seamless, safe, and truly
              memorable.
            </p>
          </div>

          {/* Closing Statement */}
          <div className="text-center mt-8">
            <p className="text-gray-700 max-w-2xl mx-auto leading-relaxed">
              Let us be part of your journey in Dubai. With{" "}
              <strong>Around The World Tourism LLC</strong>, your unforgettable
              UAE adventure begins here.
            </p>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              What Our{" "}
              <span className="bg-gradient-to-r from-blue-600 to-green-500 bg-clip-text text-transparent">
                Travelers Say
              </span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-gray-50 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star
                      key={i}
                      className="w-5 h-5 text-blue-500 fill-current"
                    />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 leading-relaxed italic">
                  "{testimonial.text}"
                </p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-green-500 rounded-full flex items-center justify-center text-white font-bold text-lg mr-4">
                    {testimonial.name.charAt(0)}
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">
                      {testimonial.name}
                    </h4>
                    <p className="text-gray-600 text-sm">
                      {testimonial.country}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        id="contact"
        className="py-20 bg-gradient-to-br from-blue-700 via-blue-600 to-green-500"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-left">
          {/* Heading */}
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight text-center">
            Ready for Your Dubai Adventure?
          </h2>
          <p className="text-lg text-white/90 mb-12 max-w-2xl mx-auto text-center">
            Contact us now and let's start planning your unforgettable journey
            in Dubai.
          </p>

          {/* Contact Info: Left Aligned on Mobile */}
          <div className="flex flex-col sm:flex-row sm:justify-start items-start gap-8 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-8 shadow-lg">
            {/* Phone */}
            <div className="flex items-center gap-4">
              <div className="bg-white rounded-full p-3">
                <Phone size={20} className="text-blue-600" />
              </div>
              <div>
                <p className="text-white/70 text-sm">Phone</p>
                <p className="text-white text-lg font-semibold">
                  +971 58 571 9601
                </p>
              </div>
            </div>

            {/* Email */}
            <div className="flex items-center gap-4">
              <div className="bg-white rounded-full p-3">
                <Mail size={20} className="text-green-600" />
              </div>
              <div>
                <p className="text-white/70 text-sm">Email</p>
                <p className="text-white text-lg font-semibold break-all">
                  atwtourismuae@gmail.com
                </p>
              </div>
            </div>

            {/* Address */}
            <div className="flex items-center gap-4">
              <div className="bg-white rounded-full p-3">
                <MapPin size={20} className="text-yellow-500" />
              </div>
              <div>
                <p className="text-white/70 text-sm">Address</p>
                <p className="text-white text-lg font-semibold">
                  FLS-63, MAKTABIK Business Centar, Al Raffa, Dubai - UAE
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white pt-16">
        {/* ✅ Full-width Branding Section with Text and Social Links */}
        <div className="w-full px-6 mb-12">
          <div className="text-center max-w-4xl mx-auto">
            <div className="text-4xl font-extrabold bg-gradient-to-r from-blue-600 to-green-500 bg-clip-text text-transparent mb-4">
              Around The World Tourism
            </div>
            <p className="text-gray-400 mb-6 leading-relaxed text-lg">
              Dubai's premier travel agency offering unforgettable experiences
              from desert safaris to luxury resorts. Creating memories that last
              a lifetime with our passionate and dedicated service.
            </p>
            <div className="flex justify-center space-x-4">
              <a
                href="https://www.facebook.com/share/16FMmjZP7s/"
                className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Facebook size={20} />
              </a>
              <a
                href="https://www.instagram.com/aroundtheworlduae"
                className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center hover:bg-green-700 transition-colors"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Instagram size={20} />
              </a>
              {/* <a href="#" className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center hover:bg-blue-600 transition-colors">
          <Twitter size={20} />
        </a> */}
            </div>
          </div>
        </div>

        {/* 📦 Main Footer Grid (still constrained) */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* You can add quick links or contact info here later */}
          </div>

          <div className="border-t border-gray-800 mt-12 pt-8 text-center">
            <p className="text-gray-400">
              © 2025 Around The World Tourism. All rights reserved. | Crafted
              with ❤️ in Dubai
            </p>
          </div>
        </div>
      </footer>

      <style jsx>{`
        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in {
          animation: fade-in 1s ease-out;
        }

        .animate-slide-up {
          animation: slide-up 1s ease-out;
        }

        .delay-100 {
          animation-delay: 0.1s;
        }

        .delay-200 {
          animation-delay: 0.2s;
        }
      `}</style>
    </div>
  );
}
